import React, { useEffect, useMemo, useState } from "react";
import { CompetitionGroup, CreatorFollowerTier } from "types/competition-analysis";
import SegmentedSwitcher, { SegmentedOption } from "Components/Common/SegmentedSwitcher";
import { formatNumber, getOrdinal, stringToColor, textToTitleCase } from "utils/helpers";
import GoldMedalIcon from "assets/icons/GoldMedal";
import RechartsGroupedBar from "Components/charts/RechartsGroupedBarChart";

type Mode =
    | "posts"
    | "views"
    | "engagements"
    | "engagement_score"
    | "creators";

const options = [
    { key: "posts", label: "Posts" },
    { key: "views", label: "Views" },
    { key: "engagements", label: "Engagements" },
    { key: "engagement_score", label: "Engagement Score" },
    { key: "creators", label: "Creators" }
] satisfies SegmentedOption<Mode>[];

const MODE_TO_FIELD: Record<Mode, string> = {
    posts: "posts",
    views: "views",
    engagements: "engagements",
    engagement_score: "engagement_score",
    creators: "creators"
};

function safeTierValue(tierData: any, tierKey: string, field: string): number {
    const v = tierData?.[tierKey]?.[field];
    return typeof v === "number" && !Number.isNaN(v) ? v : 0;
}

const CreatorBreakdown: React.FC<{ competitionGroup: CompetitionGroup, tiers: CreatorFollowerTier[] }> = ({ competitionGroup, tiers }) => {
    const [mode, setMode] = useState<Mode>("creators");
    const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({});
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const accounts = competitionGroup.accounts || [];

    // Init visibility
    useEffect(() => {
        const map: Record<string, boolean> = {};
        accounts.forEach(a => {
            map[a.social_account.uuid] = true;
        });
        setVisibleMap(map);
    }, [accounts]);

    const brands = useMemo(() => {
        return accounts.map(acc => ({
            id: acc.social_account.uuid,
            name: acc.social_account.name,
            handle: `@${acc.social_account.handle}`,
            avatar: acc.social_account.profile_picture_url,
            color: stringToColor(acc.social_account.handle),
            tierData: acc.creator_follower_tier_insight
        }));
    }, [accounts]);

    const categories = brands.map(b => b.name);
    const field = MODE_TO_FIELD[mode];

    const toggleBrand = (id: string) => {
        setVisibleMap(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const tierRanks = useMemo(() => {
        const ranks: Record<string, Record<string, number>> = {};

        tiers.forEach(t => {
            const sorted = [...brands].sort(
                (a, b) =>
                    safeTierValue(b.tierData, t.slug, field) -
                    safeTierValue(a.tierData, t.slug, field)
            );

            ranks[t.slug] = {};
            sorted.forEach((b, i) => {
                ranks[t.slug][b.id] = i + 1;
            });
        });

        return ranks;
    }, [tiers, brands, field]);

    const groupedChartData = useMemo(() => {
        return tiers.map(tier => {
            const row: any = {
                tierKey: tier.slug,
                tierLabel: textToTitleCase(tier.name),
                tierRange: `${formatNumber(tier.minimum)} - ${formatNumber(tier.maximum)}`
            };

            brands.forEach(b => {
                row[b.name] =
                    visibleMap[b.id] === false
                        ? 0
                        : safeTierValue(b.tierData, tier.slug, field);
            });

            return row;
        });
    }, [tiers, brands, field, visibleMap]);


    return (
        <div className="card shadow m-3 mt-5">
            <div className="card-header bg-purple-light p-4 d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 heading dashboard-card-heading d-flex align-items-center gap-2">
                    <span className="dashboard-card-icon">
                        <i className="bx bxs-bar-chart-alt-2 mt-1"></i>
                    </span>
                    Creator Breakdown
                </h5>

                <SegmentedSwitcher
                    options={options}
                    value={mode}
                    onChange={setMode}
                />
            </div>

            {/* CHARTS ROW */}
            <div className="p-4 py-5 pt-0 pb-0">
                <RechartsGroupedBar
                    data={groupedChartData}
                    series={brands.map(b => ({
                        key: b.name,
                        color: b.color
                    }))}
                    valueLabel={textToTitleCase(mode)}
                    height={250}
                />
            </div>

            {/* TABLE */}
            <div className="p-3 pt-0">
                <table className="table table-bordered table-hover align-middle">
                    <thead className="text-muted">
                        <tr>
                            <th>Creators</th>
                            {tiers.map(t => (
                                <th className="" key={t.slug}>{textToTitleCase(t.name)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {brands.map((b, i) => (
                            <tr
                                key={b.id}
                                onClick={() => toggleBrand(b.id)}
                                onMouseEnter={() => setHoveredIndex(i)}
                                onMouseLeave={() => setHoveredIndex(null)}
                                style={{
                                    cursor: "pointer",
                                    opacity: visibleMap[b.id] === false ? 0.4 : 1
                                }}
                            >
                                <td>
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            background:
                                                visibleMap[b.id] === false ? "#d1d5db" : b.color,
                                            marginRight: 8
                                        }}
                                    />
                                    <strong>{b.name}</strong>
                                </td>

                                {tiers.map(t => {
                                    const value = safeTierValue(b.tierData, t.slug, field);
                                    const rank = tierRanks[t.slug]?.[b.id];

                                    return (
                                        <td>
                                            <div className="rank-cell">
                                                <span className="rank-value">
                                                    {formatNumber(value)}
                                                </span>

                                                {rank === 1 ? (
                                                    <span className="rank-icon rank-icon--static"><GoldMedalIcon size={24} /></span>
                                                ) : hoveredIndex === i ? (
                                                    <span className="rank-icon rank-icon--hover text-muted">
                                                        {rank}{getOrdinal(rank)}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CreatorBreakdown;
